import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, UserCircle } from "lucide-react";

export default function EmployeesPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ username: "", first_name: "", last_name: "", email: "", phone: "", title: "", password: "", brand: "" });
  const [editingPerms, setEditingPerms] = useState(null);

  const loadEmployees = () => {
    api.get("/employees")
      .then(({ data }) => setEmployees(data.employees))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadEmployees(); }, []);

  const handleCreate = async () => {
    if (!form.username || !form.first_name || !form.last_name || !form.password) {
      toast.error("Fill required fields"); return;
    }
    try {
      await api.post("/employees", form);
      toast.success("Employee created");
      setShowCreate(false);
      setForm({ username: "", first_name: "", last_name: "", email: "", phone: "", title: "", password: "", brand: "" });
      loadEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    }
  };

  const toggleActive = async (emp) => {
    try {
      await api.put(`/employees/${emp.id}`, { active: !emp.active });
      toast.success(emp.active ? "Deactivated" : "Activated");
      loadEmployees();
    } catch { toast.error("Failed"); }
  };

  const updatePermission = async (empId, perm, value) => {
    try {
      const emp = employees.find((e) => e.id === empId);
      const perms = { ...emp.permissions, [perm]: value };
      await api.put(`/employees/${empId}`, { permissions: perms });
      loadEmployees();
      toast.success("Permission updated");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-bold tracking-tight">Users</h1>
        <Button data-testid="create-employee-btn" size="sm" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> Add Employee
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : employees.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No employees yet</div>
      ) : (
        <div className="space-y-3">
          {employees.map((emp) => (
            <Card key={emp.id} data-testid={`employee-card-${emp.id}`} className="border-border">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <UserCircle size={20} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-medium">{emp.first_name} {emp.last_name}</div>
                      <div className="text-sm text-muted-foreground">@{emp.username} {emp.title && `- ${emp.title}`}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={emp.active ? "default" : "secondary"}>{emp.active ? "Active" : "Inactive"}</Badge>
                    <Badge variant="outline" className="capitalize">{emp.brand || "N/A"}</Badge>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Active</span>
                      <Switch
                        data-testid={`toggle-active-${emp.id}`}
                        checked={emp.active}
                        onCheckedChange={() => toggleActive(emp)}
                      />
                    </div>
                  </div>
                </div>
                {/* Permissions */}
                <div className="mt-3 flex flex-wrap gap-3 text-xs">
                  <div className="flex items-center gap-1.5">
                    <Switch
                      data-testid={`perm-contracts-${emp.id}`}
                      checked={emp.permissions?.can_send_contracts}
                      onCheckedChange={(v) => updatePermission(emp.id, "can_send_contracts", v)}
                      className="scale-75"
                    />
                    <span className="text-muted-foreground">Send Contracts</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Switch
                      data-testid={`perm-release-${emp.id}`}
                      checked={emp.permissions?.can_release_reservations}
                      onCheckedChange={(v) => updatePermission(emp.id, "can_release_reservations", v)}
                      className="scale-75"
                    />
                    <span className="text-muted-foreground">Release Reservations</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Employee</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="emp-username" placeholder="Username *" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="emp-first-name" placeholder="First name *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <Input data-testid="emp-last-name" placeholder="Last name *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <Input data-testid="emp-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input data-testid="emp-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Input data-testid="emp-title" placeholder="Role / Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Input data-testid="emp-password" type="password" placeholder="Temporary password *" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            {user?.role === "super_admin" && (
              <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
                <SelectTrigger data-testid="emp-brand-select">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rrdc">Rolling River Day Camp</SelectItem>
                  <SelectItem value="riverview">Riverview Athletics</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="emp-create-confirm" onClick={handleCreate}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
